// FILE: src/contexts/AuthContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from '../services/authService'

const AuthContext = createContext(null)

const LS_TOKEN_KEY = 'token'
const LS_USER_KEY = 'session_user'

const BASE_CURRENCY_BY_COUNTRY = {
    US: 'USD',
    USA: 'USD',
    UK: 'GBP',
    GB: 'GBP',
    CA: 'CAD',
    AU: 'AUD',
    FR: 'EUR',
    SL: 'SLE',
    'SIERRA LEONE': 'SLE',
    LR: 'LRD',
    LIBERIA: 'LRD',
    GN: 'GNF',
    GUINEA: 'GNF',
    GM: 'GMD',
    GAMBIA: 'GMD',
}

const getBaseCurrency = (countryCode) => {
    const key = String(countryCode || '').trim().toUpperCase()
    return BASE_CURRENCY_BY_COUNTRY[key] || 'USD'
}

const safeRedirect = (value, fallback) => {
    const target = String(value || '').trim()
    if (!target || !target.startsWith('/') || target.startsWith('//')) {
        return fallback
    }
    return target
}

const normalizeUser = (userLike = {}) => {
    const country =
        userLike?.country ||
        userLike?.profile?.country ||
        userLike?.meta?.country ||
        'US'

    const baseCurrency =
        userLike?.baseCurrency ||
        userLike?.profile?.baseCurrency ||
        userLike?.meta?.baseCurrency ||
        getBaseCurrency(country)

    const name =
        userLike?.name ||
        `${userLike?.firstName || ''} ${userLike?.lastName || ''}`.trim() ||
        'User'

    return {
        ...userLike,
        name,
        country,
        baseCurrency,
    }
}

const initialState = {
    user: null,
    token: localStorage.getItem(LS_TOKEN_KEY),
    isAuthenticated: false,
    loading: true,
    error: null,
}

function authReducer(state, action) {
    switch (action.type) {
        case 'AUTH_CHECK_START':
            return { ...state, loading: true, error: null }

        case 'LOGIN_START':
            return { ...state, loading: true, error: null }

        case 'LOGIN_SUCCESS':
            return {
                ...state,
                user: action.payload.user,
                token: action.payload.token,
                isAuthenticated: true,
                loading: false,
                error: null,
            }

        case 'LOGIN_FAILURE':
            return {
                ...state,
                user: null,
                token: null,
                isAuthenticated: false,
                loading: false,
                error: action.payload,
            }

        case 'LOGOUT':
            return {
                ...state,
                user: null,
                token: null,
                isAuthenticated: false,
                loading: false,
                error: null,
            }

        case 'UPDATE_USER':
            return { ...state, user: normalizeUser({ ...state.user, ...action.payload }) }

        case 'CLEAR_ERROR':
            return { ...state, error: null }

        case 'AUTH_CHECK_DONE':
            return { ...state, loading: false }

        default:
            return state
    }
}

export function AuthProvider({ children }) {
    const [state, dispatch] = useReducer(authReducer, initialState)
    const navigate = useNavigate()

    useEffect(() => {
        const checkAuth = async () => {
            dispatch({ type: 'AUTH_CHECK_START' })

            const token = localStorage.getItem(LS_TOKEN_KEY)
            const savedUserRaw = localStorage.getItem(LS_USER_KEY)

            if (!token) {
                dispatch({ type: 'AUTH_CHECK_DONE' })
                return
            }

            // Real token: try server first, fallback to saved user if server fails
            try {
                const userFromApi = await authService.getProfile()
                const normalized = normalizeUser(userFromApi)

                localStorage.setItem(LS_USER_KEY, JSON.stringify(normalized))
                dispatch({ type: 'LOGIN_SUCCESS', payload: { user: normalized, token } })
            } catch (error) {
                try {
                    if (savedUserRaw) {
                        const saved = normalizeUser(JSON.parse(savedUserRaw))
                        dispatch({ type: 'LOGIN_SUCCESS', payload: { user: saved, token } })
                    } else {
                        localStorage.removeItem(LS_TOKEN_KEY)
                        localStorage.removeItem(LS_USER_KEY)
                        dispatch({ type: 'LOGOUT' })
                    }
                } catch (e) {
                    localStorage.removeItem(LS_TOKEN_KEY)
                    localStorage.removeItem(LS_USER_KEY)
                    dispatch({ type: 'LOGOUT' })
                }
            } finally {
                dispatch({ type: 'AUTH_CHECK_DONE' })
            }
        }

        checkAuth()
    }, [])

    const login = async (credentials, options = {}) => {
        dispatch({ type: 'LOGIN_START' })
        try {
            const data = await authService.login(credentials)

            const token = data?.token
            const user = normalizeUser(data?.user)

            localStorage.setItem(LS_TOKEN_KEY, token)
            localStorage.setItem(LS_USER_KEY, JSON.stringify(user))

            dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } })

            const defaultRoute =
                user?.role === 'merchant'
                    ? '/invoices'
                    : user?.role === 'government'
                        ? '/government/dashboard'
                        : '/dashboard'
            navigate(safeRedirect(options.redirectTo, defaultRoute))
            return data
        } catch (error) {
            dispatch({
                type: 'LOGIN_FAILURE',
                payload: error?.response?.data?.message || 'Login failed',
            })
            throw error
        }
    }

    const register = async (userData) => {
        dispatch({ type: 'LOGIN_START' })
        try {
            const data = await authService.register(userData)

            const token = data?.token
            const user = normalizeUser(data?.user)

            localStorage.setItem(LS_TOKEN_KEY, token)
            localStorage.setItem(LS_USER_KEY, JSON.stringify(user))

            dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } })

            navigate(
                user?.role === 'merchant'
                    ? '/invoices'
                    : user?.role === 'government'
                        ? '/government/dashboard'
                        : '/dashboard'
            )
            return data
        } catch (error) {
            dispatch({
                type: 'LOGIN_FAILURE',
                payload: error?.response?.data?.message || 'Registration failed',
            })
            throw error
        }
    }

    const logout = (options = {}) => {
        const redirectTo = options.redirectTo || '/'
        localStorage.removeItem(LS_TOKEN_KEY)
        localStorage.removeItem(LS_USER_KEY)
        dispatch({ type: 'LOGOUT' })
        navigate(redirectTo)
    }

    const updateProfile = async (userData) => {
        const data = await authService.updateProfile(userData)

        const rawUser = data?.user || data
        const updated = normalizeUser(rawUser)

        localStorage.setItem(LS_USER_KEY, JSON.stringify(updated))
        dispatch({ type: 'UPDATE_USER', payload: updated })

        return data
    }

    const uploadAvatar = async (file) => {
        const data = await authService.uploadAvatar(file)

        const rawUser = data?.user || data
        const updated = normalizeUser(rawUser)

        localStorage.setItem(LS_USER_KEY, JSON.stringify(updated))
        dispatch({ type: 'UPDATE_USER', payload: updated })

        return data
    }

    const clearError = () => dispatch({ type: 'CLEAR_ERROR' })

    const value = {
        ...state,
        login,
        register,
        logout,
        updateProfile,
        uploadAvatar,
        clearError,
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
    return ctx
}
